import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { filter, map, tap } from 'rxjs';
import {
  REVIEW_PERMISSIONS,
  ReviewPermission,
  SyllabusPermissionBySyllabusId,
  UserReviewModeBindingsHttpService,
} from '@examdojo/review';
import { entries } from '@examdojo/core/util/object-utils';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { UserRole } from '@examdojo/models/user';
import { AdminUserQuery } from '../admin-user.query';
import { SyllabusPermissionStringBySyllabusId } from '../review-permissions-list-view/review-permissions-list-view.model';

@Injectable()
export class AdminUserDetailFormService {
  constructor(
    private readonly adminUserQuery: AdminUserQuery,
    private readonly userReviewModeBindingsHttpService: UserReviewModeBindingsHttpService,
  ) {
    this.setFormValuesOnActiveUserChange().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly reviewPermissionsForm = new FormControl<SyllabusPermissionStringBySyllabusId[]>([], {
    nonNullable: true,
  });

  readonly roleControl = new FormControl<UserRole>('STUDENT', { nonNullable: true });

  readonly form = new FormGroup({
    role: this.roleControl,
    allowedModesWithSyllabusId: this.reviewPermissionsForm,
  });

  togglePermissionMode(syllabusPermissionBySyllabusId: SyllabusPermissionStringBySyllabusId) {
    const allowedModesWithSyllabusId = this.reviewPermissionsForm.value;
    const index = allowedModesWithSyllabusId.indexOf(syllabusPermissionBySyllabusId);
    if (index === -1) {
      allowedModesWithSyllabusId.push(syllabusPermissionBySyllabusId);
    } else {
      allowedModesWithSyllabusId.splice(index, 1);
    }

    this.reviewPermissionsForm.setValue(allowedModesWithSyllabusId);
    this.reviewPermissionsForm.markAsDirty();
  }

  updateUserReviewPermissions() {
    const activeAdminUser = this.adminUserQuery.getActive();
    assertNonNullable(activeAdminUser, 'activeAdminUser');

    const currentSelection = this.reviewPermissionsForm.value;

    const selectedReviewPermissions = currentSelection.reduce(
      (acc, syllabusPermissionString: SyllabusPermissionStringBySyllabusId) => {
        const updatedReviewPermissions = REVIEW_PERMISSIONS.reduce(
          (reviewAcc, reviewPermissionKey) => {
            reviewAcc[reviewPermissionKey] = false;
            return reviewAcc;
          },
          {} as SyllabusPermissionBySyllabusId['permissions'],
        );

        const [syllabusId, reviewMode] = syllabusPermissionString.split(':') as [string, ReviewPermission];
        const syllabusIdNumber = parseInt(syllabusId, 10);

        const existingEntry = acc.find((entry) => entry.syllabusId === syllabusIdNumber);

        if (existingEntry) {
          existingEntry.permissions[reviewMode] = true;
        } else {
          updatedReviewPermissions[reviewMode] = true;
          acc.push({
            syllabusId: syllabusIdNumber,
            permissions: updatedReviewPermissions,
          });
        }

        return acc;
      },
      [] as Array<{ syllabusId: number; permissions: Record<ReviewPermission, boolean> }>,
    );

    return this.userReviewModeBindingsHttpService
      .updateUsersReviewPermissions(activeAdminUser.id!, selectedReviewPermissions)
      .pipe(map(() => selectedReviewPermissions));
  }

  private setFormValuesOnActiveUserChange() {
    return this.adminUserQuery.selectActive().pipe(
      filter(Boolean),
      tap((user) => {
        const allowedModesWithSyllabusId = (user.assigned_syllabuses ?? []).flatMap((syllabusPermission) => {
          return entries(syllabusPermission)
            .map(([key, value]) => {
              if (REVIEW_PERMISSIONS.includes(key as ReviewPermission) && !!value) {
                return `${syllabusPermission.syllabus_id}:${key}` as SyllabusPermissionStringBySyllabusId;
              }

              return null;
            })
            .filter(isNotNullish);
        });

        this.form.patchValue(
          {
            role: user.role ?? 'STUDENT',
            allowedModesWithSyllabusId: allowedModesWithSyllabusId,
          },
          { emitEvent: false },
        );
      }),
    );
  }
}
